import { Amplify, Auth, API } from "aws-amplify";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import isBetween from "dayjs/plugin/isBetween";
import duration from "dayjs/plugin/duration";
import advancedFormat from "dayjs/plugin/advancedFormat";
import CryptoJS from "crypto-js";
import mixpanel from "mixpanel-browser";
import relativeTime from "dayjs/plugin/relativeTime";
import isToday from "dayjs/plugin/isToday";

import {
  APIRoutes,
  amplifyConfigData,
  amplifyConfigDataNewSetUp,
  amplifyGoConfigData,
  apiName,
  apiNameCDL,
  apiNameCDLDev,
} from "./config";

import { APIProviderFactory } from "./hexagon/adapters/driven/providers/apiProvider";
import { ClinicRepositoryFactory } from "./hexagon/adapters/driven/repositories/clinicRepository";
import { AuthServiceImplFactory } from "./hexagon/core/impl/authServiceImpl";
import { AuthProviderFactory } from "./hexagon/adapters/driven/providers/authProvider";
import { ClinicServiceImplFactory } from "./hexagon/core/impl/clinicServiceImpl";
import { LocalStorageProviderFactory } from "./hexagon/adapters/driven/providers/localStorageProvider";
import { ClinicControllerFactory } from "./hexagon/adapters/driver/clinicController";
import { AlertRepositoryFactory } from "./hexagon/adapters/driven/repositories/alertRepository";
import { AlertControllerFactory } from "./hexagon/adapters/driver/alertController";
import { AlertServiceImplFactory } from "./hexagon/core/impl/alertServiceImpl";
import { ExternalStorageProviderFactory } from "./hexagon/adapters/driven/providers/externalStorageProvider";
import { AuthControllerFactory } from "./hexagon/adapters/driver/authController";
import { TimeProviderFactory } from "./hexagon/adapters/driven/providers/timeProvider";
import { AmplifyProviderFactory } from "./hexagon/adapters/driven/providers/amplifyProvider";
import { JSONUtilFactory } from "./hexagon/adapters/driven/providers/jsonUtilProvider";
import { CryptoProviderFactory } from "./hexagon/adapters/driven/providers/cryptoFactory";
import { CircadiaEngineProviderFactory } from "./hexagon/adapters/driven/providers/circadiaEngineProvider";
import { PsnAlertRepositoryFactory } from "./hexagon/adapters/driven/repositories/psnAlertRepository";
import { PsnAlertServiceImplFactory } from "./hexagon/core/impl/psnAlertServiceImpl";
import { PsnAlertControllerFactory } from "./hexagon/adapters/driver/psnAlertController";
import { SupportControllerFactory } from "./hexagon/adapters/driver/supportController";
import { SupportRepositoryFactory } from "./hexagon/adapters/driven/repositories/supportRepository";
import { SupportServiceImplFactory } from "./hexagon/core/impl/supportServiceImpl";
import { PushNotificationProviderFactory } from "./hexagon/adapters/driven/providers/pushNotificationProvider";
import { PatientRepositoryFactory } from "./hexagon/adapters/driven/repositories/patientRepository";
import { PatientServiceImplFactory } from "./hexagon/core/impl/patientServiceImpl";
import { PatientControllerFactory } from "./hexagon/adapters/driver/patientController";
import { NotificationRepositoryFactory } from "./hexagon/adapters/driven/repositories/notificationRepository";
import { NotificationServiceImplFactory } from "./hexagon/core/impl/notificationServiceImpl";
import { NotificationControllerFactory } from "./hexagon/adapters/driver/notificationController";
import { RespRateRepositoryFactory } from "./hexagon/adapters/driven/repositories/respRateRepository";
import { RespRateServiceImplFactory } from "./hexagon/core/impl/respRateServiceImpl";
import { RespRateControllerFactory } from "./hexagon/adapters/driver/respRateController";
import { AnalyticsProviderFactory } from "./hexagon/adapters/driven/providers/AnalyticsProvider";
import { AnalyticsControllerFactory } from "./hexagon/adapters/driver/analyticsController";
import { CDLServiceImplFactory } from "./hexagon/core/impl/cdlServiceImpl";
import { CDLRepositoryFactory } from "./hexagon/adapters/driven/repositories/cdlRepository";
import { TimeDecompressionServiceImplFactory } from "./hexagon/core/impl/timeDecompressionServiceImpl";
import { UserControllerFactory } from "./hexagon/adapters/driver/userController";
import { UserServiceImplFactory } from "./hexagon/core/impl/userServiceImpl";
import { UserRepositoryFactory } from "./hexagon/adapters/driven/repositories/userRepository";
import { webIntercomProviderFactory } from "./hexagon/adapters/driven/providers/webIntercomProvider";
import { Observable } from "./util/observable";
import { TrackingRepositoryFactory } from "./hexagon/adapters/driven/repositories/trackingRepository";
import { TrackingServiceImplFactory } from "./hexagon/core/impl/trackingServiceImpl";
import { TrackingControllerFactory } from "./hexagon/adapters/driver/trackingController";
import { SummaryRepositoryFactory } from "./hexagon/adapters/driven/repositories/summaryRepository";
import { SummaryServiceImplFactory } from "./hexagon/core/impl/summaryServiceImpl";
import { SummaryControllerFactory } from "./hexagon/adapters/driver/summaryController";
import { ElectronEngineProviderFactory } from "./hexagon/adapters/driven/providers/electronEngineProvider";
import { HomeHealthControllerFactory } from "./hexagon/adapters/driver/homeHealthController";
import { HomeHealthServiceImplFactory } from "./hexagon/core/impl/homeHealthServiceImpl";
import { HomeHealthRepositoryFactory } from "./hexagon/adapters/driven/repositories/homeHealthRepository";
import { SalesRepositoryFactory } from "./hexagon/adapters/driven/repositories/salesRepository";
import { SalesServiceImplFactory } from "./hexagon/core/impl/salesServiceImpl";
import { SalesControllerFactory } from "./hexagon/adapters/driver/salesController";
import { SMSRepositoryFactory } from "./hexagon/adapters/driven/repositories/smsRepository";
import { SMSServiceImplFactory } from "./hexagon/core/impl/smsServiceImpl";
import { SMSControllerFactory } from "./hexagon/adapters/driver/smsController";
import { DeviceRepositoryFactory } from "./hexagon/adapters/driven/repositories/deviceRepository";
import { DeviceServiceImplFactory } from "./hexagon/core/impl/deviceServiceImpl";
import { DeviceControllerFactory } from "./hexagon/adapters/driver/deviceController";

import { HealthScribeWebSocketProvider } from "./hexagon/adapters/driven/providers/healthScribeWebSocketProvider";
import { HealthScribeAudioProvider } from "./hexagon/adapters/driven/providers/healthScribeAudioProvider";
import { HealthScribeRepositoryFactory } from "./hexagon/adapters/driven/repositories/healthScribeRepository";
import { HealthScribeServiceFactory } from "./hexagon/core/impl/healthScribeServiceImpl";
import { HealthScribeControllerFactory } from "./hexagon/adapters/driver/healthScribeController";
import { HealthScribeS3UploadProvider } from "./hexagon/adapters/driven/providers/healthScribeS3UploadProvider";
import { PrescriptionRepositoryFactory } from "./hexagon/adapters/driven/repositories/prescriptionRepository";
import { PrescriptionServiceImplFactory } from "./hexagon/core/impl/prescriptionServiceImpl";
import { PrescriptionControllerFactory } from "./hexagon/adapters/driver/prescriptionController";
import { ReminderRepositoryFactory } from "./hexagon/adapters/driven/repositories/reminderRepository";
import { ReminderServiceImplFactory } from "./hexagon/core/impl/reminderServiceImpl";
import { ReminderControllerFactory } from "./hexagon/adapters/driver/reminderController";
import { SelfSignupRepositoryFactory } from "./hexagon/adapters/driven/repositories/selfSignupRepository";
import { SelfSignupServiceImplFactory } from "./hexagon/core/impl/selfSignupServiceImpl";
import { SelfSignupControllerFactory } from "./hexagon/adapters/driver/selfSignupController";
import { SetupRepositoryFactory } from "./hexagon/adapters/driven/repositories/setupRepository";
import { SetupServiceImplFactory } from "./hexagon/core/impl/setupServiceImpl";
import { SetupControllerFactory } from "./hexagon/adapters/driver/setupController";

export const amplifyProvider = AmplifyProviderFactory({
  amplify: Amplify,
  auth: Auth,
  amplifyConfig: {
    amplifyConfigData,
    amplifyConfigDataNewSetUp,
    amplifyGoConfigData,
  },
});
amplifyProvider.amplifyConfigure();

export const cryptoJsProvider = (secretKey: string) =>
  CryptoProviderFactory({
    cryptoJS: CryptoJS,
    secretKey: secretKey,
  });

export const circadiaEngineProvider = CircadiaEngineProviderFactory({
  circadia: window.circadiaapp as ICircadiaEngine,
});

export const electronEngineProvider = ElectronEngineProviderFactory({
  electron: window.electron,
});

export const webIntercomProvider = webIntercomProviderFactory({
  globWindow: window,
});

export const analyticsProvider = AnalyticsProviderFactory(mixpanel);

export const analyticsController = AnalyticsControllerFactory({
  analyticsProvider: analyticsProvider,
  circadiaEngineProvider: circadiaEngineProvider,
  webIntercomProvider: webIntercomProvider,
});

export const APIProvider = APIProviderFactory({
  API,
  apiName,
  analyticsController,
});

export const CDLAPIProvider = APIProviderFactory({
  API,
  apiName: apiNameCDL,
  analyticsController,
});

export const CDLDevAPIProvider = APIProviderFactory({
  API,
  apiName: apiNameCDLDev,
  analyticsController,
});

export const timeProvider = TimeProviderFactory({
  dateLibrary: dayjs,
  utcPlugin: utc,
  timezonePlugin: timezone,
  isBetween,
  advancedFormat,
  relativeTime,
  duration,
  isToday,
});

export const AuthObserver = new Observable({ type: "initial", data: null });
if (
  circadiaEngineProvider.isWindowCircadia() &&
  circadiaEngineProvider.intfv >= 5
  // && circadiaEngineProvider.isIOS
) {
  if (circadiaEngineProvider.setCognitoLoginDoneCb)
    circadiaEngineProvider.setCognitoLoginDoneCb(() => {
      AuthObserver.update({ type: "login", payload: { success: true } });
    });

  if (circadiaEngineProvider.setCognitoLoginFailedCb)
    circadiaEngineProvider.setCognitoLoginFailedCb((error) => {
      AuthObserver.update({
        type: "login",
        payload: { success: false, error: error?.Msg },
      });
    });

  if (circadiaEngineProvider.setCognitoMfaRequiredCb)
    circadiaEngineProvider.setCognitoMfaRequiredCb((props) => {
      AuthObserver.update({
        type: "mfaRequired",
        payload: { success: false, props: props?.CODE_DELIVERY_DESTINATION },
      });
    });

  if (circadiaEngineProvider.setCognitoPinRequiredCb)
    circadiaEngineProvider.setCognitoPinRequiredCb(() => {
      AuthObserver.update({ type: "pinRequired", payload: null });
    });

  if (circadiaEngineProvider.setCognitoNewPasswordRequiredCb)
    circadiaEngineProvider.setCognitoNewPasswordRequiredCb((attrs) => {
      AuthObserver.update({ type: "newPasswordRequired", payload: attrs });
    });

  if (circadiaEngineProvider.setCognitoEmailOTPRequiredCb)
    circadiaEngineProvider.setCognitoEmailOTPRequiredCb(() => {
      AuthObserver.update({ type: "passwordlessOTPRequired", payload: null });
    });

  if (circadiaEngineProvider.setCognitoSmsOTPRequiredCb)
    circadiaEngineProvider.setCognitoSmsOTPRequiredCb((data) => {
      AuthObserver.update({
        type: "smsOTPRequired",
        payload: { success: false, props: data?.destination },
      });
    });
}

export const authProvider = AuthProviderFactory({
  Auth: amplifyProvider.auth,
  CircadiaEngine: circadiaEngineProvider,
  ElectronEngine: electronEngineProvider,
});

const AuthService = AuthServiceImplFactory({
  authProvider,
  circadiaEngineProvider,
  electronEngineProvider,
  AuthObserver,
});

export const jsonUtil = JSONUtilFactory({ JSON });
export const localStorageProvider = LocalStorageProviderFactory({
  localStorage: localStorage,
});

export const pushNotificationProvider = PushNotificationProviderFactory({
  circadiaEngineProvider,
  localStorageProvider,
  APIProvider,
  amplifyProvider,
  apiRoute: APIRoutes.apiStoreToken,
  AuthService,
});

export const externalStorageProvider = ExternalStorageProviderFactory({
  APIProvider,
  APIPath: APIRoutes.apiGetFileFromS3,
  AuthService,
});

// Notification
const notificationRepository = NotificationRepositoryFactory({
  amplifyProvider,
  APIProvider,
  AuthService,
  APIRoutes,
});

const notificationService = NotificationServiceImplFactory({
  notificationRepository,
});

export const notificationController = NotificationControllerFactory({
  notificationService,
});

// Clinic
const clinicRepository = ClinicRepositoryFactory({
  APIProvider,
  APIRoutes,
  AuthService,
});

const clinicService = ClinicServiceImplFactory({
  clinicRepository,
  localStorageProvider,
  notificationService,
});
//

// Alert
const alertRepository = AlertRepositoryFactory({
  APIProvider,
  AuthService,
  APIRoutes,
});

const alertService = AlertServiceImplFactory({
  alertRepository,
  externalStorageProvider,
});
//

// PSN Alert
const psnAlertRepository = PsnAlertRepositoryFactory({
  APIProvider,
  AuthService,
  APIRoutes,
});

const psnAlertService = PsnAlertServiceImplFactory({
  psnAlertRepository,
});
//

// Patient
const patientRepository = PatientRepositoryFactory({
  APIProvider,
  AuthService,
  APIRoutes,
});

const patientService = PatientServiceImplFactory({
  patientRepository,
});

const timeDecompressionService = TimeDecompressionServiceImplFactory();

export const cdlRepository = CDLRepositoryFactory({
  APIProvider: CDLAPIProvider,
  APIProvider2: CDLDevAPIProvider,
  AuthService,
  APIRoutes,
  timeDecompressionService,
  timeProvider,
});

const cdlService = CDLServiceImplFactory({ cdlRepository });

export const cdlService_exported = cdlService;

export const patientController = PatientControllerFactory({
  patientService,
  timeProvider,
  cdlService,
});
//

// Resprate
const respRateRepository = RespRateRepositoryFactory({
  APIProvider,
  APIRoutes,
  AuthService,
});

const respRateService = RespRateServiceImplFactory({
  respRateRepository,
});

export const respRateController = RespRateControllerFactory({
  respRateService,
});
//

// User
const userRepository = UserRepositoryFactory({
  APIProvider,
  APIRoutes,
  AuthService,
});
const userService = UserServiceImplFactory({ userRepository });

export const userController = UserControllerFactory({
  userService,
});

export const clinicController = ClinicControllerFactory({
  clinicService,
});
export const alertController = AlertControllerFactory({
  alertService,
  externalStorageProvider,
});
export const psnAlertController = PsnAlertControllerFactory({
  psnAlertService,
});

export const authController = AuthControllerFactory({
  AuthService,
  localStorageProvider,
  cryptoJsProvider,
  AuthObserver,
  circadiaEngineProvider,
  electronEngineProvider,
});

// Z-Pax

const supportRepository = SupportRepositoryFactory({
  APIProvider,
  APIRoutes,
  AuthService,
});

const supportService = SupportServiceImplFactory({
  supportRepository,
});

export const supportController = SupportControllerFactory({
  supportService,
});

const trackingRepository = TrackingRepositoryFactory({
  APIProvider,
  APIRoutes,
  AuthService,
});

const trackingService = TrackingServiceImplFactory({
  trackingRepository,
});

export const trackingController = TrackingControllerFactory({
  trackingService,
});

const summaryRepository = SummaryRepositoryFactory({
  APIProvider,
  APIRoutes,
  AuthService,
});

const summaryService = SummaryServiceImplFactory({
  summaryRepository,
});

export const summaryController = SummaryControllerFactory({
  summaryService,
});

// Home Health
const homeHealthRepository = HomeHealthRepositoryFactory({
  APIProvider,
  APIRoutes,
  AuthService,
});

const homeHealthService = HomeHealthServiceImplFactory({
  homeHealthRepository,
});

export const homeHealthController = HomeHealthControllerFactory({
  homeHealthService,
});

//sales
const salesRepository = SalesRepositoryFactory({
  APIProvider,
  APIRoutes,
  AuthService,
});

const salesService = SalesServiceImplFactory({ salesRepository });

export const salesController = SalesControllerFactory({
  salesService,
});

// SMS
const smsRepository = SMSRepositoryFactory({
  APIProvider,
  APIRoutes,
  AuthService,
  amplifyProvider,
});

const smsService = SMSServiceImplFactory({ smsRepository });

export const smsController = SMSControllerFactory({
  smsService,
});

// HealthScribe Components
export const healthScribeWebSocketProvider =
  new HealthScribeWebSocketProvider();
export const healthScribeAudioProvider = new HealthScribeAudioProvider();

const healthScribeRepository = HealthScribeRepositoryFactory({
  apiProvider: APIProvider,
  authService: authController,
});

export const healthScribeS3UploadProvider = new HealthScribeS3UploadProvider(
  healthScribeRepository
);

const healthScribeService = HealthScribeServiceFactory({
  healthScribeRepository,
  healthScribeWebSocketProvider,
  healthScribeAudioProvider,
  s3UploadProvider: healthScribeS3UploadProvider,
});

export const healthScribeController = HealthScribeControllerFactory({
  healthScribeService,
  healthScribeWebSocketProvider,
  healthScribeAudioProvider,
  s3UploadProvider: healthScribeS3UploadProvider,
});

// Prescriptions
const prescriptionRepository = PrescriptionRepositoryFactory({
  APIProvider,
  AuthService,
  APIRoutes,
});

const prescriptionService = PrescriptionServiceImplFactory({
  prescriptionRepository,
});

export const prescriptionController = PrescriptionControllerFactory({
  prescriptionService,
});

// Reminders
const reminderRepository = ReminderRepositoryFactory({
  APIProvider,
  AuthService,
  APIRoutes,
});

const reminderService = ReminderServiceImplFactory({
  reminderRepository,
});

export const reminderController = ReminderControllerFactory({
  reminderService,
});

// Device
const deviceRepository = DeviceRepositoryFactory({
  APIProvider,
  AuthService,
  APIRoutes,
});

const deviceService = DeviceServiceImplFactory({ deviceRepository });

export const deviceController = DeviceControllerFactory({ deviceService });

// Self-Signup
const selfSignupRepository = SelfSignupRepositoryFactory({
  APIProvider,
  AuthService,
  APIRoutes,
});

const selfSignupService = SelfSignupServiceImplFactory({
  selfSignupRepository,
});

export const selfSignupController = SelfSignupControllerFactory({
  selfSignupService,
});

// Field Setup (technician wizard) — admin endpoints on patient-centric-app-v2
const setupRepository = SetupRepositoryFactory({
  AuthService,
});

const setupService = SetupServiceImplFactory({
  setupRepository,
});

export const setupController = SetupControllerFactory({
  setupService,
});
